import React, { memo } from "react";

import { Grid } from "@material-ui/core";
import { useField } from "formik";
import { useSelector } from "react-redux";

import { SubtleBadge } from "components/Badge";
import { BusinessBuyerSplitsSection } from "components/Business/BusinessForm/FinanceAndAccounting/BuyerSplitCollapse";
import { BusinessGeneralFinanceSection } from "components/Business/BusinessForm/FinanceAndAccounting/GeneralCollapse";
import { BusinessInsuranceSection } from "components/Business/BusinessForm/FinanceAndAccounting/InsuranceCollapse";
import { BusinessInterestSection } from "components/Business/BusinessForm/FinanceAndAccounting/InterestCollapse";
import { BusinessVendorCommissionSection } from "components/Business/BusinessForm/FinanceAndAccounting/VendorCommissionCollapse";
import { BusinessVendorSplitsSection } from "components/Business/BusinessForm/FinanceAndAccounting/VendorSplitCollapse";
import { CollapseLabel, FormCollapse } from "components/Form";
import { withNamespace } from "components/Form/FormikControls";
import { Row } from "components/Layout";

import { DeploymentPermissions } from "constants/permissions";
import { FinanceSubModalSection } from "constants/settings";

import { FormColumn } from "containers/Settings/Deployment";
import { TradingTermsSection } from "containers/Settings/Deployment/Finance/TradingTerms";

import { getGSTStatusText } from "lib/businesses";

import { getDefaultBillingSettings } from "selectors";

import {
  useHasDefaultBuyerSplitPermission,
  useHasDefaultVendorSplitPermission,
  useHasDeploymentPermission,
  useHasTradingTermsPermission,
  useSectionToggle,
} from "hooks";

const Header = ({ namespace: ns }) => {
  const [{ value: isABNActive }] = useField(withNamespace(ns, "isABNActive"));
  const [{ value: isGstRegistered }] = useField(
    withNamespace(ns, "isGSTRegistered"),
  );
  const [{ value: abrLastChecked }] = useField(
    withNamespace(ns, "abrLastChecked"),
  );

  const [{ value: shouldChargeGSTOverride }] = useField(
    withNamespace(ns, "shouldChargeGSTOverride"),
  );

  return (
    <Row>
      <SubtleBadge>Finance & Accounting</SubtleBadge>
      <CollapseLabel>
        GST:{" "}
        {getGSTStatusText(
          isABNActive,
          isGstRegistered,
          abrLastChecked,
          shouldChargeGSTOverride,
        )}
      </CollapseLabel>
    </Row>
  );
};

const FinanceAndAccountingComponent = ({
  namespace: ns,
  isOpen,
  onToggle,
  abn,
  businessId,
  abrLastChecked,
}) => {
  const billingSettings = useSelector(getDefaultBillingSettings);

  const hasDefaultVendorSplitFeature = useHasDefaultVendorSplitPermission();
  const hasDefaultBuyerSplitFeature = useHasDefaultBuyerSplitPermission();
  const hasInterestFeature = useHasDeploymentPermission(
    DeploymentPermissions.featureInterest,
  );

  const hasVendorCommissionsFeature = useHasDeploymentPermission(
    DeploymentPermissions.featureVendorCommissions,
  );

  const interestEnabled =
    hasInterestFeature && billingSettings && billingSettings.enableInterest;

  const hasTradingTermsFeature = useHasTradingTermsPermission();

  const subSectionHelper = useSectionToggle(FinanceSubModalSection.GENERAL);

  return (
    <FormCollapse
      isOpen={isOpen}
      onToggle={onToggle}
      header={<Header namespace={ns} />}
      dataTour="financeAccountingCollapse"
    >
      <Grid item xs={12} md={12}>
        <Grid container spacing={2}>
          <FormColumn>
            <BusinessGeneralFinanceSection
              sectionHelper={subSectionHelper}
              businessId={businessId}
              namespace={ns}
              abn={abn}
              abrLastChecked={abrLastChecked}
            />
            <BusinessInsuranceSection
              sectionHelper={subSectionHelper}
              namespace={ns}
            />
            {interestEnabled && (
              <BusinessInterestSection
                sectionHelper={subSectionHelper}
                namespace={ns}
              />
            )}
            {hasVendorCommissionsFeature && (
              <BusinessVendorCommissionSection
                sectionHelper={subSectionHelper}
                namespace={ns}
                businessId={businessId}
              />
            )}
            {hasDefaultVendorSplitFeature && (
              <BusinessVendorSplitsSection sectionHelper={subSectionHelper} />
            )}
            {hasDefaultBuyerSplitFeature && (
              <BusinessBuyerSplitsSection sectionHelper={subSectionHelper} />
            )}
            {hasTradingTermsFeature && (
              <TradingTermsSection
                sectionHelper={subSectionHelper}
                namespace={ns}
              />
            )}
          </FormColumn>
        </Grid>
      </Grid>
    </FormCollapse>
  );
};

export default memo(FinanceAndAccountingComponent);
